import { useEffect, useState } from 'react';
import { useBuffStore, type ActiveBuff } from '@/state/buffStore';

function fmtDuration(sec: number): string {
  if (sec <= 0) return '0s';
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  if (h > 0) return `${h}h${m.toString().padStart(2, '0')}m`;
  if (m > 0) return `${m}m${s.toString().padStart(2, '0')}s`;
  return `${s}s`;
}

const KIND_META: Record<string, { label: string; color: string; icon: string }> = {
  exp: { label: 'EXP', color: '#ffeb3b', icon: '/assets/ui/buffs/exp.png' },
  drop: { label: 'DROP', color: '#7ad7ff', icon: '/assets/ui/buffs/drop.png' },
};

export function BuffBar() {
  const buffs = useBuffStore((s) => s.buffs);
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, []);

  const now = Date.now();
  const visible: (ActiveBuff & { remaining: number })[] = buffs
    .map((b) => ({ ...b, remaining: Math.max(0, b.remainingS - Math.floor((now - b.fetchedAt) / 1000)) }))
    .filter((b) => b.remaining > 0);

  if (visible.length === 0) return null;

  return (
    <div style={{
      display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 6,
      marginTop: 6,
    }}>
      {visible.map((b) => {
        const meta = KIND_META[b.kind] ?? { label: b.kind.toUpperCase(), color: '#cfd8dc', icon: '' };
        return (
          <div
            key={b.id}
            title={`${meta.label} ×${b.multiplier.toFixed(1)} - hết sau ${fmtDuration(b.remaining)}`}
            style={{
              position: 'relative',
              width: 48,
              height: 48,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'flex-end',
              filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.55))',
              fontSize: 10,
              color: '#fff',
              userSelect: 'none',
            }}
          >
            {meta.icon ? (
              <img
                src={meta.icon}
                alt={meta.label}
                draggable={false}
                style={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  imageRendering: 'auto',
                  pointerEvents: 'none',
                }}
              />
            ) : (
              <div style={{
                position: 'absolute', inset: 0,
                background: 'rgba(11,18,32,0.85)',
                border: `1px solid ${meta.color}`,
                borderRadius: 6,
              }} />
            )}
            <span style={{
              position: 'absolute',
              top: -2,
              right: -2,
              padding: '1px 4px',
              background: 'rgba(11,18,32,0.92)',
              border: `1px solid ${meta.color}`,
              borderRadius: 4,
              color: meta.color,
              fontWeight: 800,
              fontSize: 10,
              lineHeight: 1.1,
              textShadow: '0 1px 1px rgba(0,0,0,0.8)',
            }}>
              ×{b.multiplier.toFixed(1)}
            </span>
            <span style={{
              position: 'relative',
              marginBottom: -2,
              padding: '0 4px',
              background: 'rgba(0,0,0,0.65)',
              borderRadius: 3,
              fontVariantNumeric: 'tabular-nums',
              fontWeight: 700,
              textShadow: '0 1px 1px rgba(0,0,0,0.9)',
            }}>
              {fmtDuration(b.remaining)}
            </span>
          </div>
        );
      })}
    </div>
  );
}
